import { Download, LayoutGrid, LogOut, Plus } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { InstallInstructions } from "@/pwa/InstallInstructions";
import { useInstall } from "@/pwa/install";
import { useOpenNewEntry } from "./NewEntry";
import { BOTTOM, type NavItem, navItems } from "./nav";
import { ThemeToggle } from "./ThemeToggle";

const itemClass =
  "flex min-h-12 min-w-14 flex-1 flex-col items-center justify-center gap-1 rounded-control text-[11px] font-medium focus-visible:outline-2 focus-visible:outline-accent";

/** Barra inferior do celular: 4 destinos + "+" central (quem pode lançar); o resto fica em "Mais". */
export function BottomNav({
  canWrite,
  email,
  platformAdmin,
  onSignOut,
}: {
  canWrite: boolean;
  email?: string;
  platformAdmin: boolean;
  onSignOut: () => void;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [installHelp, setInstallHelp] = useState(false);
  const install = useInstall();
  const newEntry = useOpenNewEntry();
  const items = navItems(platformAdmin);
  const main = items.filter((i) => BOTTOM.has(i.to));
  const rest = items.filter((i) => !BOTTOM.has(i.to));
  // /w/:wid/<seção>/... — "Mais" é a seção atual quando a tela aberta é uma das que ele agrupa.
  const section = useLocation().pathname.split("/")[3] ?? "";
  const inMore = rest.some((i) => i.to === section);
  const link = (i: NavItem) => (
    <NavLink
      key={i.label}
      to={i.to}
      end={i.end}
      className={({ isActive }) => cn(itemClass, isActive ? "text-accent-soft-ink" : "text-muted")}
    >
      <i.Icon aria-hidden="true" className="size-[22px]" />
      {i.label}
    </NavLink>
  );

  return (
    <nav
      aria-label="Principal"
      className="fixed inset-x-0 bottom-0 z-40 flex items-start justify-around border-t border-border bg-surface pt-2 pr-[calc(env(safe-area-inset-right)_+_0.5rem)] pb-[max(env(safe-area-inset-bottom),12px)] pl-[calc(env(safe-area-inset-left)_+_0.5rem)]"
    >
      {main.slice(0, 2).map(link)}
      {canWrite && (
        <button
          type="button"
          {...newEntry.triggerProps}
          onClick={newEntry.open}
          aria-label="Novo lançamento"
          className="-mt-6 flex size-14 shrink-0 items-center justify-center rounded-card bg-accent text-accent-ink shadow-float focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Plus aria-hidden="true" className="size-7" strokeWidth={2.4} />
        </button>
      )}
      {main.slice(2).map(link)}
      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerTrigger
          aria-current={inMore ? "page" : undefined}
          className={cn(itemClass, inMore ? "text-accent-soft-ink" : "text-muted")}
        >
          <LayoutGrid aria-hidden="true" className="size-[22px]" />
          Mais
        </DrawerTrigger>
        <DrawerContent aria-describedby={undefined}>
          <div className="flex flex-col gap-5 overflow-y-auto px-4 pt-1 pb-6">
            <DrawerTitle>Mais</DrawerTitle>
            <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0">
              {rest.map((i) => (
                <li key={i.label}>
                  <Link
                    to={i.to}
                    onClick={() => setMoreOpen(false)}
                    className="flex min-h-22 flex-col items-center justify-center gap-2 rounded-card bg-surface-2 px-2 text-center text-[13px] font-medium text-ink focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    <i.Icon aria-hidden="true" className="size-6 text-accent-soft-ink" />
                    {i.label}
                  </Link>
                </li>
              ))}
            </ul>
            {install.available && (
              <Button
                variant="secondary"
                className="justify-start"
                onClick={() => {
                  if (install.canPrompt) {
                    void install.prompt();
                    return;
                  }
                  setMoreOpen(false);
                  setInstallHelp(true);
                }}
              >
                <Download aria-hidden="true" className="size-4" />
                Instalar app
              </Button>
            )}
            <ThemeToggle />
            <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
              <span className="min-w-0 truncate text-sm text-muted">{email}</span>
              <Button variant="secondary" onClick={onSignOut}>
                <LogOut aria-hidden="true" className="size-4" />
                Sair
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      <InstallInstructions open={installHelp} onOpenChange={setInstallHelp} platform={install.platform} />
    </nav>
  );
}
